import React, { useEffect, useState } from 'react';
import MetaTags from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardTitle,
  Table,
  Progress,
} from 'reactstrap';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import CountUp from 'react-countup';
import ReactApexChart from 'react-apexcharts';
import { DashBreadcrumb } from '../../../components/Common/Breadcrumb';
import { fetchServiceOrderStats } from '../ManageServiceOrders/actions/action';
import wpLoader from '../../../assets/WP.mp4';

const formatNumber = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const StarRating = ({ rating }) => {
  const rounded = Math.round(Number(rating) || 0);
  return (
    <span
      className="text-nowrap"
      title={`${Number(rating || 0).toFixed(1)} / 5`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <i
          key={i}
          className={`bx ${i <= rounded ? 'bxs-star' : 'bx-star'}`}
          style={{ color: '#e0a53d', fontSize: '15px' }}
        ></i>
      ))}
    </span>
  );
};

const ServiceOrderDashboard = () => {
  const dispatch = useDispatch();
  const currentCountry = localStorage.getItem('WEBSITE_COUNTRY') || 'ae';
  let cntry = currentCountry === 'all' ? 'ae' : currentCountry.toUpperCase();

  const [startDate, setStartDate] = useState(
    moment().subtract(6, 'months').format('YYYY-MM-DD'),
  );
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  useEffect(() => {
    fetchServiceOrderStats({
      params: {
        date_from: startDate,
        date_to: endDate,
        country: cntry.toLowerCase(),
      },
      dispatch,
    });
  }, [startDate, endDate, cntry, dispatch]);

  const { serviceOrderStats, isServiceStatsLoading } = useSelector(
    (state) => state.serviceDashboard,
  );

  if (isServiceStatsLoading) {
    return (
      <div
        className="page-content d-flex flex-column justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <video
          src={wpLoader}
          autoPlay
          muted
          loop
          playsInline
          style={{ width: '200px', height: 'auto' }}
        />
        <h5 className="mt-3 text-muted fw-normal">Please wait...</h5>
      </div>
    );
  }

  const graphData = serviceOrderStats?.data?.graph || [];
  const stats = serviceOrderStats?.data?.stats || [];
  const trends = serviceOrderStats?.data?.trends || {};
  const technicians = serviceOrderStats?.data?.technicians || [];

  const getCount = (id) => {
    const item = stats.find((s) => s.status == id);
    return item ? item.count : 0;
  };

  const renderTrend = (value) => {
    if (value === undefined || value === null) return null;
    const isPositive = value >= 0;
    return (
      <div className="mt-3">
        <span
          className={`badge bg-soft-${
            isPositive ? 'success' : 'danger'
          } text-${isPositive ? 'success' : 'danger'} p-1`}
        >
          <i
            className={`bx bx-trending-${
              isPositive ? 'up' : 'down'
            } align-middle me-1`}
          ></i>
          {isPositive ? '+' : ''}
          {value}%
        </span>
        <span className="ms-2 text-muted font-size-13 text-nowrap">
          from previous period
        </span>
      </div>
    );
  };

  // Mapping stats
  const pendingCount = getCount(11);
  const assignedCount = getCount(12);
  const completedCount = getCount(15);
  const cancelledCount = getCount(2);

  // Mapping graph data - bucketed by week (Monday start date) by the API
  const weeks = [...new Set(graphData.map((d) => d.date))].sort();

  const prepareSeriesData = (statusId) => {
    // A given week/status can have several rows - one per payment_mode
    // split (e.g. Resolved is broken out by cash/card/free/credit) - so
    // every matching row must be summed, not just the first one found.
    return weeks.map((week) => {
      return graphData
        .filter((d) => d.date === week && d.status === statusId)
        .reduce((sum, entry) => sum + parseInt(entry.count || 0), 0);
    });
  };

  const revenueByPaymentMode = (paymentMode) =>
    graphData
      .filter(
        (d) =>
          d.payment_mode &&
          d.payment_mode.toLowerCase() === paymentMode.toLowerCase(),
      )
      .reduce((sum, entry) => sum + parseFloat(entry.revenue || 0), 0);

  const prepareRevenueData = (paymentMode) => {
    return weeks.map((week) => {
      const entries = graphData.filter(
        (d) =>
          d.date === week &&
          d.payment_mode &&
          d.payment_mode.toLowerCase() === paymentMode.toLowerCase(),
      );
      return entries.reduce(
        (sum, entry) => sum + parseFloat(entry.revenue || 0),
        0,
      );
    });
  };

  const totalCashCollected = revenueByPaymentMode('cash');
  const totalCardCollected = revenueByPaymentMode('payfort');

  const weekLabels = weeks.map((w) => `${moment(w).format('MMM DD')}`);

  const chartOptions = {
    chart: {
      id: 'service-jobs-chart',
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '38%',
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: { show: true, width: 2, colors: ['transparent'] },
    colors: ['#e0a53d', '#3b5de7', '#2e9d6f', '#f06548'], // Amber for Pending, Blue for Assigned, Green for Resolved, Red for Canceled
    xaxis: {
      categories: weekLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    dataLabels: { enabled: false },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (value) => `${formatNumber(value)} jobs` },
    },
    legend: { position: 'top', horizontalAlign: 'right' },
    fill: { opacity: 1 },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
  };

  const chartSeries = [
    {
      name: 'Pending',
      data: prepareSeriesData('11'),
    },
    {
      name: 'Assigned',
      data: prepareSeriesData('12'),
    },
    {
      name: 'Resolved',
      data: prepareSeriesData('15'),
    },
    {
      name: 'Canceled',
      data: prepareSeriesData('2'),
    },
  ];

  const revenueChartOptions = {
    chart: {
      id: 'revenue-trend-chart',
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '38%',
        borderRadius: 6,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: { show: true, width: 2, colors: ['transparent'] },
    colors: ['#3b5de7', '#0b9b51', '#50a5f1', '#e0a53d'], // Colors for Cash, Card, Free, Credit
    xaxis: {
      categories: weekLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    dataLabels: { enabled: false },
    yaxis: {
      labels: {
        formatter: (value) => formatNumber(value),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (value) => formatNumber(value) },
    },
    fill: { opacity: 1 },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    legend: { position: 'top', horizontalAlign: 'right' },
  };

  const revenueChartSeries = [
    {
      name: 'Cash',
      data: prepareRevenueData('cash'),
    },
    {
      name: 'Card',
      data: prepareRevenueData('payfort'),
    },
    {
      name: 'Free',
      data: prepareRevenueData('free'),
    },
    {
      name: 'Credit',
      data: prepareRevenueData('credit'),
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Service Order Dashboard | DH OMS</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col>
              <DashBreadcrumb
                title="Service Orders"
                breadcrumbItem="Service Order Dashboard"
              />
            </Col>
            <Col className="text-end">
              <DateRangePicker
                placeholder="Select Date Range"
                placement="bottomEnd"
                container={() => document.body}
                onChange={(dates) => {
                  if (dates) {
                    setStartDate(moment(dates[0]).format('YYYY-MM-DD'));
                    setEndDate(moment(dates[1]).format('YYYY-MM-DD'));
                  }
                }}
              />
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>
              <span className="text-muted font-size-12 ms-1">
                Showing the Result from{' '}
                <span className="text-info fw-medium">
                  {moment(startDate).format('Do MMM YYYY')} -{' '}
                  {moment(endDate).format('Do MMM YYYY')}
                </span>
              </span>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={3}>
              <Card
                className="card-h-100 border shadow-sm"
                style={{ borderRadius: '12px', borderColor: '#e9e9ef' }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <span className="text-muted mb-2 lh-1 d-block text-truncate font-size-14">
                        Pending Jobs
                      </span>
                      <h4 className="mb-0">
                        <span className="counter-value">
                          <CountUp end={parseInt(pendingCount)} duration={2} />
                        </span>
                      </h4>
                    </div>
                    <div
                      className="avatar-md text-center"
                      style={{
                        backgroundColor: '#fff7e6',
                        color: '#ffb311',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}
                    >
                      <i className="bx bx-time-five"></i>
                    </div>
                  </div>
                  {renderTrend(trends.pending)}
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                className="card-h-100 border shadow-sm"
                style={{ borderRadius: '12px', borderColor: '#e9e9ef' }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <span className="text-muted mb-2 lh-1 d-block text-truncate font-size-14">
                        Assigned Jobs
                      </span>
                      <h4 className="mb-0">
                        <span className="counter-value">
                          <CountUp end={parseInt(assignedCount)} duration={2} />
                        </span>
                      </h4>
                    </div>
                    <div
                      className="avatar-md text-center"
                      style={{
                        backgroundColor: '#e6f0ff',
                        color: '#1170ff',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}
                    >
                      <i className="bx bx-clipboard"></i>
                    </div>
                  </div>
                  {renderTrend(trends.assigned)}
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                className="card-h-100 border shadow-sm"
                style={{ borderRadius: '12px', borderColor: '#e9e9ef' }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <span className="text-muted mb-2 lh-1 d-block text-truncate font-size-14">
                        Completed Jobs
                      </span>
                      <h4 className="mb-0">
                        <span className="counter-value">
                          <CountUp
                            end={parseInt(completedCount)}
                            duration={2}
                          />
                        </span>
                      </h4>
                    </div>
                    <div
                      className="avatar-md text-center"
                      style={{
                        backgroundColor: '#e6f9f4',
                        color: '#50c0a8',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}
                    >
                      <i className="bx bx-check-circle"></i>
                    </div>
                  </div>
                  {renderTrend(trends.completed)}
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                className="card-h-100 border shadow-sm"
                style={{ borderRadius: '12px', borderColor: '#e9e9ef' }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <span className="text-muted mb-2 lh-1 d-block text-truncate font-size-14">
                        Cancelled Jobs
                      </span>
                      <h4 className="mb-0">
                        <span className="counter-value">
                          <CountUp
                            end={parseInt(cancelledCount)}
                            duration={2}
                          />
                        </span>
                      </h4>
                    </div>
                    <div
                      className="avatar-md text-center"
                      style={{
                        backgroundColor: '#fff4f6',
                        color: '#e15e7d',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}
                    >
                      <i className="bx bx-x-circle"></i>
                    </div>
                  </div>
                  {renderTrend(trends.cancelled)}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col md={6}>
              <Card
                className="card-h-100 border shadow-sm"
                style={{ borderRadius: '12px', borderColor: '#e9e9ef' }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <span className="text-muted mb-2 lh-1 d-block text-truncate font-size-14">
                        Cash Collected
                      </span>
                      <h4 className="mb-0">
                        {formatNumber(totalCashCollected)}
                      </h4>
                    </div>
                    <div
                      className="avatar-md text-center"
                      style={{
                        backgroundColor: '#e6ebfc',
                        color: '#3b5de7',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}
                    >
                      <i className="bx bx-money"></i>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="text-muted font-size-13">
                      Total cash collections for the selected period
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card
                className="card-h-100 border shadow-sm"
                style={{ borderRadius: '12px', borderColor: '#e9e9ef' }}
              >
                <CardBody className="p-4">
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <span className="text-muted mb-2 lh-1 d-block text-truncate font-size-14">
                        Card Collected
                      </span>
                      <h4 className="mb-0">
                        {formatNumber(totalCardCollected)}
                      </h4>
                    </div>
                    <div
                      className="avatar-md text-center"
                      style={{
                        backgroundColor: '#e6f5ec',
                        color: '#0b9b51',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}
                    >
                      <i className="bx bx-credit-card"></i>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="text-muted font-size-13">
                      Total card collections for the selected period
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xl={6}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">Jobs Status Comparison</CardTitle>
                  <ReactApexChart
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    height={350}
                  />
                </CardBody>
              </Card>
            </Col>
            <Col xl={6}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">Revenue Trend</CardTitle>
                  <ReactApexChart
                    options={revenueChartOptions}
                    series={revenueChartSeries}
                    type="bar"
                    height={350}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xl={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-1">Technician Performance</CardTitle>
                  <p className="text-muted font-size-13 mb-1">
                    Jobs assigned, completed and average customer rating for the
                    selected period
                  </p>
                  <p className="text-muted font-size-12 mb-4">
                    {moment(startDate).format('Do MMM YYYY')} -{' '}
                    {moment(endDate).format('Do MMM YYYY')}
                  </p>
                  {technicians.length === 0 ? (
                    <p className="text-muted mb-0">
                      No technician activity in the selected period.
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle mb-0">
                        <thead>
                          <tr className="text-muted font-size-13">
                            <th>Technician</th>
                            <th>Assigned</th>
                            <th>Pending</th>
                            <th>In Progress</th>
                            <th>Completed</th>
                            <th>Rescheduled</th>
                            <th>Cancelled</th>
                            <th style={{ minWidth: '180px' }}>
                              Completion Rate
                            </th>
                            <th>Rating</th>
                          </tr>
                        </thead>
                        <tbody>
                          {technicians.map((tech) => {
                            const assigned = parseInt(tech.total_assigned) || 0;
                            const pending = parseInt(tech.total_pending) || 0;
                            const inProgress =
                              parseInt(tech.total_in_progress) || 0;
                            const completed =
                              parseInt(tech.total_completed) || 0;
                            const rescheduled =
                              parseInt(tech.total_rescheduled) || 0;
                            const cancelled =
                              parseInt(tech.total_cancelled) || 0;
                            const completionRate = assigned
                              ? Math.round((completed / assigned) * 100)
                              : 0;
                            return (
                              <tr key={tech.emp_id}>
                                <td>
                                  <span className="fw-medium">
                                    {tech.technician_name || tech.emp_id}
                                  </span>
                                </td>
                                <td>{formatNumber(assigned)}</td>
                                <td>{formatNumber(pending)}</td>
                                <td>{formatNumber(inProgress)}</td>
                                <td>{formatNumber(completed)}</td>
                                <td>{formatNumber(rescheduled)}</td>
                                <td>{formatNumber(cancelled)}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <Progress
                                      value={completionRate}
                                      style={{ height: '6px', flex: 1 }}
                                      color={
                                        completionRate >= 75
                                          ? 'success'
                                          : completionRate >= 40
                                            ? 'warning'
                                            : 'danger'
                                      }
                                    />
                                    <span className="text-muted font-size-13">
                                      {completionRate}%
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  {tech.avg_rating ? (
                                    <StarRating rating={tech.avg_rating} />
                                  ) : (
                                    <span className="text-muted font-size-13">
                                      No ratings yet
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ServiceOrderDashboard;
